import {useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useEffect, useMemo, useRef} from 'react'
import {getEnv} from '@github-ui/client-env'
// eslint-disable-next-line no-restricted-imports -- This shared loader runs outside ReportErrorContextProvider trees.
import {reportError} from '@github-ui/failbot'

import {
  ENTERPRISE_MANAGED_SETTINGS_ERROR,
  parseEnterpriseManagedSettings,
  type EnterpriseManagedSettingsInput,
} from './enterprise-managed-settings'

const reportedInvalidSettings = new WeakSet<object>()

export function useEnterpriseManagedSettings({
  enterpriseManagedSettings,
  enterpriseManagedSettingsUrl,
  currentUserLogin,
}: EnterpriseManagedSettingsInput = {}) {
  const initial = useMemo(() => {
    try {
      if (enterpriseManagedSettings === undefined) {
        if (enterpriseManagedSettingsUrl !== undefined) throw new Error('Missing initial enterprise-managed settings')
        return {settings: undefined}
      }
      const settings = parseEnterpriseManagedSettings(enterpriseManagedSettings)
      if (settings.status === 'pending' && !enterpriseManagedSettingsUrl) {
        throw new Error('Missing enterprise-managed settings URL')
      }
      return {settings}
    } catch (error) {
      return {settings: ENTERPRISE_MANAGED_SETTINGS_ERROR, error}
    }
  }, [enterpriseManagedSettings, enterpriseManagedSettingsUrl])
  const reportedInitialError = useRef<unknown>(undefined)
  useEffect(() => {
    if (initial.error && reportedInitialError.current !== initial.error) {
      reportedInitialError.current = initial.error
      if (typeof enterpriseManagedSettings === 'object' && enterpriseManagedSettings !== null) {
        if (reportedInvalidSettings.has(enterpriseManagedSettings)) return
        reportedInvalidSettings.add(enterpriseManagedSettings)
      }
      reportError(initial.error, {message: 'Invalid initial enterprise-managed settings'})
    }
  }, [enterpriseManagedSettings, initial.error])
  const pending = initial.settings?.status === 'pending'
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- Viewer settings must not be persisted. The response is validated before use.
  const query = useQuery({
    queryKey: ['copilot-enterprise-managed-settings', currentUserLogin ?? getEnv().login, enterpriseManagedSettingsUrl],
    queryFn: async ({signal}) => {
      try {
        if (!enterpriseManagedSettingsUrl) throw new Error('Missing enterprise-managed settings URL')
        const response = await reactFetchJSON(enterpriseManagedSettingsUrl, {signal, cache: 'no-store'})
        if (!response.ok) throw new Error(`Failed to load enterprise-managed settings: ${response.status}`)
        const data: unknown = await response.json()
        if (typeof data !== 'object' || data === null || !('enterpriseManagedSettings' in data)) {
          throw new Error('Missing enterprise-managed settings response')
        }
        const settings = parseEnterpriseManagedSettings(data.enterpriseManagedSettings)
        if (settings.status === 'pending') throw new Error('Enterprise-managed settings did not resolve')
        return settings
      } catch (error) {
        if (!signal.aborted) reportError(error, {message: 'Failed to load enterprise-managed settings'})
        throw error
      }
    },
    enabled: pending && Boolean(enterpriseManagedSettingsUrl),
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })

  if (!pending) return initial.settings
  if (!enterpriseManagedSettingsUrl || query.isError) return ENTERPRISE_MANAGED_SETTINGS_ERROR
  return query.data ?? initial.settings
}
